import type { CostVsBudgetChartData } from '../../api/financials-client/financials-client.type';

const COST_VS_OUTPUT_SCHEMA = {
  c: 'cost',
  db: 'daily_burn',
  tc: 'tasks_completed',
  tco: 'tasks_committed',
  cpt: 'cost_per_task',
  pc: 'points_completed',
  pco: 'points_committed',
  cpp: 'cost_per_point',
  m: 'month',
  y: 'year',
  g: 'grade',
  l: 'label',
  p: 'project_name',
  pid: 'project_id',
} as const;

type FlatSummary = {
  c: number;
  db: number;
  tc: number;
  tco: number;
  cpt: number;
  pc: number;
  pco: number;
  cpp: number;
};

type FlatPeriod = {
  m: number;
  y: number;
  g: number;
  l: string;
};

type FlatProjectData = {
  m: number;
  y: number;
  pid: string;
  p: string;
  c: number;
  g: number;
  db: number;
  tc: number;
  tco: number;
  pc: number;
  pco: number;
};

/**
 * Flattened structure optimized for TOON encoding.
 */
type FlattenedCostVsOutputData = {
  schema: typeof COST_VS_OUTPUT_SCHEMA;
  last: FlatSummary | null;
  avg: FlatSummary | null;
  periods: FlatPeriod[];
  projectData: FlatProjectData[];
};

enum CostsVsOutputChartItemLabels {
  cost = 'Cost',
  costTrend = 'CostTrend',
  dailyBurn = 'DailyBurn',
  dailyBurnTrend = 'DailyBurnTrend',
  throughPut = 'ThroughPut',
  throughPutTrend = 'ThroughPutTrend',
  tasksCommited = 'TasksCommited',
  tasksCommitedTrend = 'TasksCommitedTrend',
  costPerTask = 'CostPerTask',
  costPerTaskTrend = 'CostPerTaskTrend',
  velocity = 'Velocity',
  velocityTrend = 'VelocityTrend',
  pointsCommitted = 'PointsCommitted',
  pointsCommittedTrend = 'PointsCommittedTrend',
  costPerPoint = 'CostPerPoint',
  costPerPointTrend = 'CostPerPointTrend',
}

type ShowCostsVsOutputChartLines = {
  showCost: boolean;
  showCostTrend: boolean;
  showDailyBurn: boolean;
  showDailyBurnTrend: boolean;
  showThroughPut: boolean;
  showThroughPutTrend: boolean;
  showTasksCommited: boolean;
  showTasksCommitedTrend: boolean;
  showCostPerTask: boolean;
  showCostPerTaskTrend: boolean;
  showVelocity: boolean;
  showVelocityTrend: boolean;
  showPointsCommitted: boolean;
  showPointsCommittedTrend: boolean;
  showCostPerPoint: boolean;
  showCostPerPointTrend: boolean;
};
type Position = 'left' | 'right' | 'center' | 'top' | 'bottom';

type SanitizedData = {
  labels: string[];
  cost: number[];
  dailyBurn: number[];
  costPerTask: number[];
  costPerPoint: number[];
  throughput: number[];
  tasksComitted: number[];
  velocity: number[];
  pointsCommitted: number[];
};

type CostsVsOutputChartProps = {
  showCostsVsOutputChartLines: ShowCostsVsOutputChartLines;
  chartData: CostVsBudgetChartData[] | null;
  workPeriodIds: string[];
};

export { COST_VS_OUTPUT_SCHEMA, CostsVsOutputChartItemLabels };
export type {
  CostsVsOutputChartProps,
  FlatPeriod,
  FlatProjectData,
  FlatSummary,
  FlattenedCostVsOutputData,
  Position,
  SanitizedData,
  ShowCostsVsOutputChartLines,
};
